import React, { useEffect, useState } from 'react';

import { Alert, Button, Flex, Paper, PaperProps, useMantineTheme } from '@mantine/core';
import { IconAlertCircle, IconFolder, IconPlus } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { BatchResourceSelector } from 'components/BatchResourceSelector/BatchResourceSelector';
import { ErrorAlert } from 'components/ErrorAlert';
import { ResourceLoadingSkeleton } from 'components/LoadingSkeletons/ResourceLoadingSkeleton';
import { NoDataFound } from 'components/NoDataFound';
import { ResourceTile } from 'components/ResourceTile';
import { SearchBar } from 'components/SearchBar';
import { VirtualListScrollArea } from 'components/VirtualListScrollArea';
import { useSubscriptionContext } from 'contexts/SubscriptionContext';
import { useTranslation } from 'react-i18next';
import { ResourceService } from 'services/resource-service';
import { MAX_FILE_COUNT } from 'shared/constants/general-constants';
import { RESOURCES_QUERY_KEY } from 'shared/constants/query-keys';
import { MY_RESOURCES_FILTER } from 'shared/constants/resource';
import { PRIMARY_SHADE_WHITE } from 'shared/constants/theme';
import { Resource, ResourceAiProcessingStatus, ResourceType, UnUploadedResource } from 'shared/interfaces/Resource';
import { isUplodedResource } from 'shared/utils/resource';

const SEARCH_BAR_BOTTOM_MARGIN = 10;

interface ResourceSectionProps extends PaperProps {
  selectedResources: Map<string, UnUploadedResource | Resource> | Set<string>;
  showMinResourcesError: boolean;
  onResourceClick: (resource: Resource | UnUploadedResource) => void;
  allowedResourceTypes: ResourceType[];
  unUploadedResources?: UnUploadedResource[];
  onBatchUpload?: (unUploadedResource: UnUploadedResource[]) => void;
  searchBarDropdownStyle?: React.CSSProperties;
  sortOnFirstLoad?: boolean;
}
export function ResourceSection({
  unUploadedResources,
  onBatchUpload,
  onResourceClick,
  selectedResources,
  showMinResourcesError,
  searchBarDropdownStyle,
  allowedResourceTypes,
  sortOnFirstLoad = false,
  ...paperProps
}: ResourceSectionProps) {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const [subscription] = useSubscriptionContext();

  const [fetchPeriodically, setFetchPeriodically] = useState(false);
  const [tagFilters, setTagFilters] = useState(new Set<string>([]));
  const [selectedTagFilters, setSelectedTagFilters] = useState(new Set<string>([]));
  const [searchValue, setSearchValue] = useState('');
  const [allResources, setAllResources] = useState<Resource[] | UnUploadedResource[]>([]);

  const [batchResourceSelectorOpened, setBatchResourceSelectorOpened] = useState(false);

  const maxResourceCount = subscription.product.resourcePerSmartAssistantLimit ?? MAX_FILE_COUNT;

  const getAllowedResources = async () => {
    const allResources = await ResourceService.getAllResourcesCreatedByMe();

    if (allowedResourceTypes.length > 0) {
      return allResources.filter((resource) => allowedResourceTypes.includes(resource.type));
    } else {
      return allResources;
    }
  };

  const resourcesQuery = useQuery({
    queryKey: [RESOURCES_QUERY_KEY, MY_RESOURCES_FILTER],
    refetchInterval: () => (fetchPeriodically ? 5000 : 0), // 5 seconds
    queryFn: getAllowedResources,
    onSuccess: (resources: Resource[]) => {
      const tags = new Set<string>();
      let shouldFetchPeriodically = false;
      for (const resource of resources) {
        if (resource.aiProcessingStatus === ResourceAiProcessingStatus.IN_PROGRESS) {
          shouldFetchPeriodically = true;
        }

        resource.tags.forEach((tag) => tags.add(tag));
      }
      setFetchPeriodically(shouldFetchPeriodically);
      setTagFilters(tags);
    }
  });

  useEffect(() => {
    const all = [...(unUploadedResources ?? []), ...(resourcesQuery.data ?? [])];
    if (sortOnFirstLoad) {
      all.sort((a, b) => {
        const aKey = isUplodedResource(a) ? a._id : a.fileName;
        const bKey = isUplodedResource(b) ? b._id : b.fileName;

        return +selectedResources.has(bKey) - +selectedResources.has(aKey);
      });
    }
    setAllResources(all);
  }, [resourcesQuery.data, unUploadedResources]);

  const CreateButton = ({ variant }: { variant?: string }) => (
    <Button variant={variant} leftIcon={<IconPlus />} size="xs" onClick={() => setBatchResourceSelectorOpened(true)}>
      {t('APP.ACTIONS.ADD_NEW')}
    </Button>
  );

  if (resourcesQuery.isError) return <ErrorAlert />;

  const resourcesLoading = resourcesQuery.isLoading;
  const noResources = !resourcesLoading && resourcesQuery.data.length === 0 && (unUploadedResources ?? []).length === 0;

  const filteredResources = [...allResources]
    .filter((resource) =>
      selectedTagFilters.size > 0 && isUplodedResource(resource)
        ? resource.tags.some((tag) => selectedTagFilters.has(tag))
        : true
    )
    .filter((resource) => (searchValue ? resource.fileName.toLowerCase().includes(searchValue.toLowerCase()) : true));

  return (
    <>
      <Paper
        {...paperProps}
        style={{
          borderColor:
            selectedResources.size > maxResourceCount || showMinResourcesError
              ? theme.colors.red[PRIMARY_SHADE_WHITE]
              : undefined,
          display: 'flex',
          flexDirection: 'column'
        }}>
        {(resourcesLoading || allResources.length > 0) && (
          <SearchBar
            mb={SEARCH_BAR_BOTTOM_MARGIN}
            filters={tagFilters}
            selectedFilters={selectedTagFilters}
            setSelectedFilters={setSelectedTagFilters}
            onSearchValueChange={setSearchValue}
            placeholder={t('SEARCH_BAR.RESOURCE_PLACEHOLDER')}
            resources={allResources}
            rightSection={
              !!searchValue || selectedTagFilters.size > 0 ? (
                <Button variant="default" size="xs" onClick={() => filteredResources.forEach(onResourceClick)}>
                  {t('APP.ACTIONS.SELECT_ALL')}
                </Button>
              ) : (
                onBatchUpload && <CreateButton variant="default" />
              )
            }
            dropdownStyle={searchBarDropdownStyle}
          />
        )}

        {resourcesLoading ? (
          <ResourceLoadingSkeleton />
        ) : (
          <VirtualListScrollArea
            h={noResources || filteredResources.length === 0 ? 0 : '100%'}
            items={filteredResources.map((resource, index) => (
              <ResourceTile
                key={index}
                mt={index === 0 ? 0 : 5}
                mb={5}
                resource={resource}
                onResourceClick={() => onResourceClick(resource)}
                optionsEnabled
                canSelect
                selected={selectedResources.has(isUplodedResource(resource) ? resource._id : resource.fileName)}
                allTags={Array.from(tagFilters)}
                newBadge={!isUplodedResource(resource)}
              />
            ))}
          />
        )}

        {noResources && (
          <NoDataFound
            h="100%"
            size="xs"
            title={t('NO_DATA_FOUND.CHATBOT_RESOURCES.TITLE')}
            subTitle={t('NO_DATA_FOUND.CHATBOT_RESOURCES.SUB_TITLE')}
            icon={<IconFolder />}
            button={onBatchUpload ? <CreateButton /> : undefined}
          />
        )}

        {!noResources && filteredResources.length == 0 && (selectedTagFilters.size > 0 || searchValue) && (
          <NoDataFound h="100%" />
        )}
      </Paper>

      {(selectedResources.size > maxResourceCount || showMinResourcesError) && (
        <Flex direction="column" align="center" justify="center" mx="auto" mt={10}>
          {selectedResources.size > maxResourceCount && (
            <Alert
              my={5}
              icon={<IconAlertCircle />}
              title={t('SMART_ASSISTANT.RESOURCES_SECTION.MAX_SELECTION.TITLE')}
              color="red"
              maw={300}>
              {t('SMART_ASSISTANT.RESOURCES_SECTION.MAX_SELECTION.DESCRIPTION', { maxFileCount: maxResourceCount })}
            </Alert>
          )}
          {showMinResourcesError && (
            <Alert
              my={5}
              icon={<IconAlertCircle />}
              title={t('SMART_ASSISTANT.RESOURCES_SECTION.NO_FILE_SELECTED.TITLE')}
              color="red"
              maw={300}>
              {t('SMART_ASSISTANT.RESOURCES_SECTION.NO_FILE_SELECTED.DESCRIPTION')}
            </Alert>
          )}
        </Flex>
      )}

      <BatchResourceSelector
        onBatchUpload={(unUploadedResources: UnUploadedResource[]) => {
          onBatchUpload?.(unUploadedResources);
        }}
        onClose={() => setBatchResourceSelectorOpened(false)}
        opened={batchResourceSelectorOpened}
      />
    </>
  );
}
